import React, { useRef, useEffect, memo, useState } from 'react';
import PersistentInput from './PersistentInput';

// Define WebSocket constants for environments where WebSocket is not available
const WS_CONSTANTS = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3
};

// Use native WebSocket or fallback constants
const WebSocketClass = typeof WebSocket !== 'undefined' ? WebSocket : {
  CONNECTING: WS_CONSTANTS.CONNECTING,
  OPEN: WS_CONSTANTS.OPEN,
  CLOSING: WS_CONSTANTS.CLOSING,
  CLOSED: WS_CONSTANTS.CLOSED
};

// Memoized component for rendering individual chat messages
const ChatMessage = memo(({ msg }) => {
  // Handle missing properties safely
  if (!msg) {
    return null;
  }

  // Get display name - prioritize username, fallback to generic name instead of wallet address
  const displayName = msg.username || 'Anonymous';

  // Get avatar letter from username or fallback to '?'
  const avatarLetter = msg.username?.charAt(0)?.toUpperCase() || '?';

  // Format timestamp
  const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : 'Unknown time';

  // Handle error state
  const hasError = msg.error === true;

  return (
    <div className={`flex items-start gap-2 ${hasError ? 'opacity-50' : ''}`}>
      <img
        src={`https://placehold.co/32x32/8B5CF6/ffffff?text=${avatarLetter}`}
        alt={displayName}
        className="w-8 h-8 rounded-full"
      />
      <div>
        <p>
          <span className="font-semibold text-purple-400">
            {displayName}
          </span>
          <span className="text-gray-400 text-sm"> · {timestamp}</span>
        </p>
        <p className="text-sm">
          {msg.message}
          {hasError && (
            <span className="text-red-400 text-xs ml-2">
              (Failed to send)
            </span>
          )}
        </p>
      </div>
    </div>
  );
});
ChatMessage.displayName = 'ChatMessage';

// Memoized component for rendering the list of chat messages
const ChatMessages = memo(({ messages }) => {
  // Handle null or undefined messages
  if (!messages || !Array.isArray(messages)) {
    return null;
  }

  return (
    <>
      {messages.map((msg) => (
        <ChatMessage key={msg.id || msg._id || Date.now() + Math.random()} msg={msg} />
      ))}
    </>
  );
});
ChatMessages.displayName = 'ChatMessages';

// Send button component with WebSocket connection awareness
const SendButton = memo(({ messageInput, handleSendMessage, isConnected = false, socketRef = null }) => {
  // Track connection status
  const [connectionState, setConnectionState] = useState(isConnected ? 'connected' : 'connecting');

  useEffect(() => {
    // Update state based on isConnected prop
    setConnectionState(isConnected ? 'connected' : 'connecting');

    // Check WebSocket state if available
    if (socketRef?.current) {
      const updateState = () => {
        if (socketRef.current.readyState === WebSocketClass.OPEN) {
          setConnectionState('connected');
        } else if (socketRef.current.readyState === WebSocketClass.CONNECTING) {
          setConnectionState('connecting');
        } else {
          setConnectionState('disconnected');
        }
      };

      // Initial check
      updateState();

      // Set up event listeners
      const handleOpen = () => setConnectionState('connected');
      const handleClose = () => setConnectionState('disconnected');

      socketRef.current.addEventListener('open', handleOpen);
      socketRef.current.addEventListener('close', handleClose);

      return () => {
        if (socketRef.current) {
          socketRef.current.removeEventListener('open', handleOpen);
          socketRef.current.removeEventListener('close', handleClose);
        }
      };
    }
  }, [isConnected, socketRef]);

  // Disable if message is empty or not connected
  const isDisabled = !messageInput.trim() || connectionState !== 'connected';

  return (
    <button
      onClick={handleSendMessage}
      disabled={isDisabled}
      className={`px-3 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl transition-all duration-200 transform hover:scale-105 disabled:scale-100 flex items-center justify-center min-w-[48px] shadow-lg hover:shadow-purple-500/25 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      title="Send message"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
      </svg>
    </button>
  );
});
SendButton.displayName = 'SendButton';

// Chat input component that uses PersistentInput to maintain focus
const ChatInput = memo(({ inputRef, messageInput, setMessageInput, handleSendMessage, isConnected = false, socketRef = null, isUserTyping = false }) => {
  // Track connection status
  const [connectionState, setConnectionState] = useState(isConnected ? 'connected' : 'connecting');

  useEffect(() => {
    // Update state based on isConnected prop
    setConnectionState(isConnected ? 'connected' : 'connecting');

    // Check WebSocket state if available
    if (socketRef?.current) {
      const updateState = () => {
        if (socketRef.current.readyState === WebSocketClass.OPEN) {
          setConnectionState('connected');
        } else if (socketRef.current.readyState === WebSocketClass.CONNECTING) {
          setConnectionState('connecting');
        } else {
          setConnectionState('disconnected');
        }
      };

      // Initial check
      updateState();

      // Set up event listeners
      const handleOpen = () => setConnectionState('connected');
      const handleClose = () => setConnectionState('disconnected');

      socketRef.current.addEventListener('open', handleOpen);
      socketRef.current.addEventListener('close', handleClose);

      return () => {
        if (socketRef.current) {
          socketRef.current.removeEventListener('open', handleOpen);
          socketRef.current.removeEventListener('close', handleClose);
        }
      };
    }
  }, [isConnected, socketRef]);

  // Use our PersistentInput component that maintains focus
  return (
    <PersistentInput
      ref={inputRef}
      placeholder={connectionState === 'connected' ? "Send a message" : "Connecting to chat..."}
      value={messageInput}
      onChange={(e) => setMessageInput(e.target.value)}
      onKeyPress={handleSendMessage}
      className={`flex-1 p-3 rounded-xl bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-200 ${connectionState !== 'connected' ? 'cursor-not-allowed opacity-50' : ''}`}
      disabled={connectionState !== 'connected'}
      isUserTyping={isUserTyping} // Pass the isUserTyping prop to PersistentInput
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.messageInput === nextProps.messageInput &&
    prevProps.isConnected === nextProps.isConnected &&
    prevProps.isUserTyping === nextProps.isUserTyping &&
    prevProps.setMessageInput === nextProps.setMessageInput &&
    prevProps.handleSendMessage === nextProps.handleSendMessage
  );
});
ChatInput.displayName = 'ChatInput';

// Connection status component for WebSocket chat
const ConnectionStatus = memo(({ isConnected = false, socketRef = null }) => {
  const [connectionState, setConnectionState] = useState(isConnected ? 'connected' : 'connecting');

  useEffect(() => {
    // Update state based on isConnected prop
    setConnectionState(isConnected ? 'connected' : 'connecting');

    // Check WebSocket state if available
    if (socketRef?.current) {
      switch (socketRef.current.readyState) {
        case WebSocketClass.CONNECTING:
          setConnectionState('connecting');
          break;
        case WebSocketClass.OPEN:
          setConnectionState('connected');
          break;
        case WebSocketClass.CLOSING:
        case WebSocketClass.CLOSED:
          setConnectionState('disconnected');
          break;
        default:
          break;
      }

      // Set up event listeners
      const handleOpen = () => setConnectionState('connected');
      const handleClose = () => setConnectionState('disconnected');
      const handleError = () => setConnectionState('error');

      socketRef.current.addEventListener('open', handleOpen);
      socketRef.current.addEventListener('close', handleClose);
      socketRef.current.addEventListener('error', handleError);

      return () => {
        if (socketRef.current) {
          socketRef.current.removeEventListener('open', handleOpen);
          socketRef.current.removeEventListener('close', handleClose);
          socketRef.current.removeEventListener('error', handleError);
        }
      };
    }
  }, [isConnected, socketRef]);

  // Render different status indicators based on connection state
  switch (connectionState) {
    case 'connected':
      return (
        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full flex items-center">
          <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
          Connected
        </span>
      );
    case 'connecting':
      return (
        <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full flex items-center">
          <span className="w-2 h-2 bg-yellow-400 rounded-full mr-1 animate-pulse"></span>
          Connecting...
        </span>
      );
    case 'disconnected':
      return (
        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full flex items-center">
          <span className="w-2 h-2 bg-red-400 rounded-full mr-1"></span>
          Disconnected
        </span>
      );
    case 'error':
      return (
        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full flex items-center">
          <span className="w-2 h-2 bg-red-400 rounded-full mr-1"></span>
          Error
        </span>
      );
    default:
      return (
        <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded-full flex items-center">
          <span className="w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
          Unknown
        </span>
      );
  }
});
ConnectionStatus.displayName = 'ConnectionStatus';

// Use React.memo to prevent unnecessary re-renders
const ChatSection = memo(({
  messages,
  chatError,
  messageInput,
  setMessageInput,
  handleSendMessage,
  user,
  messagesEndRef,
  streamId,
  isConnected = true,
  socketRef = null,
  isUserTyping = false
}) => {
  // Create a ref for our PersistentInput component
  const inputRef = useRef(null);
  return (
    <div className="h-[calc(100vh-2rem)]">
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 h-full flex flex-col overflow-hidden">
        {/* Chat header */}
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 p-4 border-b border-gray-700/50">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Stream Chat</h2>
                <p className="text-xs text-gray-400">Join the conversation</p>
              </div>
            </div>
            <ConnectionStatus isConnected={isConnected} socketRef={socketRef} />
          </div>
        </div>

        {/* Chat messages */}
        <div className="flex-1 overflow-y-auto mb-4 space-y-4">
          {chatError && (
            <div className="bg-red-500/20 text-red-400 p-2 rounded text-sm mb-4">
              {chatError}
            </div>
          )}
          {!messages || messages.length === 0 ? (
            <div className="text-gray-400 text-center py-4">
              No messages yet. Be the first to chat!
            </div>
          ) : (
            <ChatMessages messages={messages} />
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Chat input */}
        <div className="border-t border-gray-700/50 p-4 bg-gray-900/30">
          {user?.isConnected ? (
            <div className="space-y-3">
              <div className="flex gap-3 items-center">
                <ChatInput
                  inputRef={inputRef}
                  messageInput={messageInput}
                  setMessageInput={setMessageInput}
                  handleSendMessage={handleSendMessage}
                  isConnected={isConnected}
                  socketRef={socketRef}
                  isUserTyping={isUserTyping}
                />
                <SendButton
                  messageInput={messageInput}
                  handleSendMessage={handleSendMessage}
                  isConnected={isConnected}
                  socketRef={socketRef}
                />
              </div>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Press Enter to send
              </p>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-gray-400 mb-2 font-medium">Join the conversation</p>
              <p className="text-gray-500 text-sm">Use the login button in the sidebar to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
ChatSection.displayName = 'ChatSection';

export default ChatSection;
